import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
// import { AuthService } from 'src/app/shared/services/auth-service.service';
import { OAuthService } from 'src/app/shared/services/oauth.service';
import { ProjectService } from '../Projects/project.service';
import { LoggerService } from 'src/app/shared/services/logger.service';
import { AnalyticsService } from 'src/app/shared/services/analytics.service';
import { SigninCallbackDiagnostics } from 'src/app/shared/services/oauth.service';
import { isChunkLoadError } from 'src/app/shared/utils/chunk-load-error';
import { navigateHandingOff } from 'src/app/shared/utils/navigate-handing-off';

@Component({
    selector: 'app-signin-callback',
    templateUrl: 'signin-callback.component.html',
    styleUrls: ['./signin-redirect-callback.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SigninRedirectCallbackComponent implements OnInit, OnDestroy {
  messageKeys: string[] = [
    "SIGNIN_LOADING_MESSAGES.LOADING_SCHEDULE",
    "SIGNIN_LOADING_MESSAGES.OPTIMIZING_ROUTES",
    "SIGNIN_LOADING_MESSAGES.SYNCING_WAREHOUSE",
    "SIGNIN_LOADING_MESSAGES.CHECKING_CRANE",
    "SIGNIN_LOADING_MESSAGES.RESCHEDULING",
    "SIGNIN_LOADING_MESSAGES.FINDING_ROUTE",
    "SIGNIN_LOADING_MESSAGES.CONFIRMING_DRIVER",
    "SIGNIN_LOADING_MESSAGES.VERIFYING_LOAD",
    "SIGNIN_LOADING_MESSAGES.TRACKING_DELIVERIES",
    "SIGNIN_LOADING_MESSAGES.ALLOCATING_STORAGE",
    "SIGNIN_LOADING_MESSAGES.CHECKING_SITE",
    "SIGNIN_LOADING_MESSAGES.HERDING_FORKLIFTS",
    "SIGNIN_LOADING_MESSAGES.LOOKING_PACKING_SLIP",
    "SIGNIN_LOADING_MESSAGES.COORDINATING_INSTALLATION",
    "SIGNIN_LOADING_MESSAGES.NEGOTIATING_GRAVITY",
    "SIGNIN_LOADING_MESSAGES.ESTIMATING_UNLOADING",
    "SIGNIN_LOADING_MESSAGES.SYNCING_INVENTORY",
    "SIGNIN_LOADING_MESSAGES.FINDING_PARKING",
    "SIGNIN_LOADING_MESSAGES.PREPARING_SITE_ACCESS",
    "SIGNIN_LOADING_MESSAGES.BALANCING_SCHEDULE",
    "SIGNIN_LOADING_MESSAGES.LOADING_MANIFEST",
    "SIGNIN_LOADING_MESSAGES.ASSIGNING_UNLOADING_CREW",
    "SIGNIN_LOADING_MESSAGES.ADJUSTING_ETA",
    "SIGNIN_LOADING_MESSAGES.SENDING_NOTIFICATIONS",
    "SIGNIN_LOADING_MESSAGES.OPTIMIZING_FORKLIFT_ROUTES",
    "SIGNIN_LOADING_MESSAGES.SYNCING_DELIVERY_STATUS",
    "SIGNIN_LOADING_MESSAGES.ALLOCATING_STORAGE_ZONES",
    "SIGNIN_LOADING_MESSAGES.ENSURING_RIGHT_MATERIALS",
    "SIGNIN_LOADING_MESSAGES.VERIFYING_SITE_CONDITIONS",
    "SIGNIN_LOADING_MESSAGES.SORTING_PRIORITIES"
  ];

  currentMessageKey: string | null = this.messageKeys[0];
  private messageInterval: ReturnType<typeof setInterval> | null = null;
  constructor(
    private _authService: OAuthService,
    private router: Router,
    private logger: LoggerService,
    private analytics: AnalyticsService
  ) {
    this.rotateMessages();
  }


  ngOnInit() {
    this._authService.finishLogin()
      .then(
        () => this.handlePostLogin(),
        // Only genuine finishLogin/OIDC failures land here. Navigation failures
        // after a successful login are handled inside handlePostLogin so they can
        // never be misread as an auth error and trigger a re-login loop.
        error => this.handleLoginError(error)
      )
      .catch(error =>
        // Last-resort guard: handlePostLogin handles its own navigation errors, so
        // reaching here means something unexpected threw. Log it, but do NOT
        // restart the login flow.
        this.logger.errorWithContext('SigninRedirectCallback', 'Unhandled post-login error', error)
      );
  }

  private async handlePostLogin(): Promise<void> {
    sessionStorage.removeItem('signin_retry_count');
    sessionStorage.removeItem('stale_build_reload_attempted');
    const redirectUrl = this._authService.redirectUrl;
    const target = redirectUrl || '/';
    try {
      // A user whose token carries no role can use nothing: go straight to the awaiting-access page
      // instead of flashing an empty home and a burst of refused calls. The saved redirect URL is kept
      // so "Check again" can take the user to the original deep link once access is granted.
      const hasAccess = await this._authService.hasAnyAccess().catch(() => true);
      if (!hasAccess) {
        await this.router.navigate(['/awaiting-access'], { replaceUrl: true });
        return;
      }
      this._authService.clearRedirectUrl();
      if (!redirectUrl) {
        await this.router.navigate(['/'], { replaceUrl: true });
        return;
      }
      await this.navigateToRedirectUrl(redirectUrl);
    } catch (error) {
      // The user is authenticated at this point — a navigation failure must be
      // handled as a navigation problem (usually a stale bundle after a redeploy),
      // never by restarting the OIDC login flow.
      this.handlePostLoginNavigationError(error, target);
    }
  }

  private async navigateToRedirectUrl(redirectUrl: string): Promise<void> {
    const urlTree = this.router.parseUrl(redirectUrl);

    if (!urlTree.root.children['primary']) {
      await this.router.navigate(['/'], { replaceUrl: true });
      return;
    }

    const pathSegments = urlTree.root.children['primary'].segments.map(segment => segment.path);
    const outcome = await navigateHandingOff(this.router, pathSegments, {
      queryParams: urlTree.queryParams,
      replaceUrl: true
    });

    if (outcome === 'rejected') {
      // Route blocked by a guard that went nowhere — land on root instead. A guard that
      // redirected (PermissionGuard → /unauthorized) is left alone: falling back to root would
      // supersede it and hide why the link did not open. Any navigation failure (e.g. a stale
      // lazy chunk) propagates to handlePostLogin's catch.
      await this.router.navigate(['/'], { replaceUrl: true });
    }
  }

  /**
   * Handle a navigation failure that occurs AFTER a successful login.
   *
   * The user is already authenticated, so we must not re-run the OIDC flow. The
   * common cause is a stale app shell after a redeploy: the hashed bundle for the
   * target route (e.g. home.component-XXXX.js) no longer exists on the server, so
   * the lazy import 404s. A hard reload fetches a fresh index.html + bundles and
   * lands the user on their intended route.
   */
  private handlePostLoginNavigationError(error: unknown, attemptedUrl: string): void {
    if (isChunkLoadError(error)) {
      this.logger.warn('SigninRedirectCallback', `Stale build after login — reloading to ${attemptedUrl}`, error);
      this.reloadForStaleBuild(attemptedUrl);
      return;
    }

    // Non-chunk failure (malformed redirect URL, guard/resolver throw). Still
    // authenticated — fall back to root rather than restarting login.
    this.logger.errorWithContext('SigninRedirectCallback', 'Navigation error after login', error);
    this.router.navigate(['/'], { replaceUrl: true }).catch(rootError => {
      if (isChunkLoadError(rootError)) {
        this.reloadForStaleBuild('/');
      } else {
        this.logger.errorWithContext('SigninRedirectCallback', 'Root fallback navigation failed after login', rootError);
      }
    });
  }

  /** Full-page reload to recover from a stale build, guarded against a reload loop. */
  private reloadForStaleBuild(attemptedUrl: string): void {
    const reloadKey = 'stale_build_reload_attempted';
    if (sessionStorage.getItem(reloadKey)) {
      // Already hard-reloaded once and the bundle still won't load. Stop here to
      // avoid a reload loop; the next uncaught chunk failure surfaces the
      // GlobalErrorHandler "new version available" prompt instead.
      this.logger.errorWithContext('SigninRedirectCallback', 'Stale build persists after reload', attemptedUrl);
      return;
    }
    sessionStorage.setItem(reloadKey, '1');
    window.location.assign(attemptedUrl.startsWith('/') ? attemptedUrl : '/');
  }

  private async handleLoginError(error: any): Promise<void> {
    const errorCode = String(error?.error ?? error?.message ?? String(error));
    const diagnostics = this._authService.lastSigninDiagnostics;

    // oidc-client deletes the signin state from storage the moment it is first read —
    // before the token exchange even runs. A second load of the same callback URL
    // (tab killed and restored by the browser, back button, reopened from history)
    // therefore always fails with "No matching state found in storage". If a valid
    // session already exists the original callback succeeded — enter the app instead
    // of restarting the OIDC flow. Nothing from the replayed URL is trusted or used.
    if (errorCode.includes('No matching state found in storage')) {
      const user = await this._authService.getUser().catch(() => null);
      if (user && !user.expired) {
        this.reportSigninStateLoss('signin_callback_replayed', diagnostics, error);
        this.logger.warn(
          'SigninRedirectCallback',
          `Replayed signin callback with valid session — entering app (${this.describe(diagnostics)})`,
          error
        );
        return this.handlePostLogin();
      }

      // No local session to fall back on. Either the callback was replayed in a tab
      // that never held one (userStore is per-tab sessionStorage), or the state was
      // gone before the first exchange ever finished. Both recover by re-running the
      // flow against the live IDP cookie, so this is expected noise rather than a
      // failure: it is tracked as an action and only escalated to the error pipeline
      // if the retries run out.
      this.reportSigninStateLoss('signin_state_unavailable', diagnostics, error);
      this.restartLoginAfterFailure(error, `Signin state unavailable (${this.describe(diagnostics)})`, 'warn');
      return;
    }

    if (errorCode.includes('access_denied')) {
      // User pressed "Cancel" on the IS4 login screen — go back to root,
      // AuthGuard will send them to login again if they try to navigate somewhere protected.
      this.logger.errorWithContext('SigninRedirectCallback', 'Error during login', error);
      this.router.navigate(['/'], { replaceUrl: true });
      return;
    }

    this.restartLoginAfterFailure(error, 'Error during login', 'error');
  }

  /**
   * Restart the OIDC flow, capped at 2 retries so an IDP that keeps failing (account
   * locked, corrupted session, timeout) can never produce a redirect loop.
   *
   * `level` is how the individual recoverable attempts are logged; exhausting the cap
   * always reaches the error pipeline, whatever level the attempts before it used.
   */
  private restartLoginAfterFailure(error: any, message: string, level: 'warn' | 'error'): void {
    const retryKey = 'signin_retry_count';
    const retryCount = +(sessionStorage.getItem(retryKey) ?? 0);

    if (level === 'error') {
      this.logger.errorWithContext('SigninRedirectCallback', message, error);
    }

    if (retryCount >= 2) {
      sessionStorage.removeItem(retryKey);
      this.logger.errorWithContext('SigninRedirectCallback', `Too many login retries — aborting: ${message}`, error);
      this.router.navigate(['/'], { replaceUrl: true });
      return;
    }

    if (level === 'warn') {
      this.logger.warn('SigninRedirectCallback', `${message} — retrying login (${retryCount + 1}/2)`, error);
    }

    sessionStorage.setItem(retryKey, String(retryCount + 1));
    this._authService.login();
  }

  /**
   * Record a recoverable signin-state failure as a tracked action rather than an error.
   *
   * LoggerService.warn() is console-only, so without this the recovery paths leave no
   * trace in production — which is what made the previous occurrences of this error
   * impossible to classify from the logs alone.
   */
  private reportSigninStateLoss(
    action: string,
    diagnostics: SigninCallbackDiagnostics | null,
    error: any
  ): void {
    this.analytics.trackAction(action, 'auth', 'SigninRedirectCallback', diagnostics?.verdict, {
      ...(diagnostics ?? {}),
      message: String(error?.message ?? error ?? ''),
      retryCount: +(sessionStorage.getItem('signin_retry_count') ?? 0)
    });
  }

  private describe(diagnostics: SigninCallbackDiagnostics | null): string {
    return diagnostics?.summary || 'no diagnostics';
  }

  ngOnDestroy(): void {
    if (this.messageInterval !== null) {
      clearInterval(this.messageInterval);
    }
  }

  private rotateMessages(): void {
    let index = 0;
    this.messageInterval = setInterval(() => {
      // Set to null first to force Angular to detect a change
      this.currentMessageKey = null;

      setTimeout(() => {
        index = (index + 1) % this.messageKeys.length;
        this.currentMessageKey = this.messageKeys[index];
      }, 500); // Wait for fade-out before updating text
    }, 3000);
  }
}
